import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Typography,
  Button,
  Box,
  Checkbox,
  FormControlLabel,
  useTheme,
  useMediaQuery,
  Divider,
  Link
} from '@mui/material';
import {
  Lock,
  Security,
  Analytics,
  ContactMail,
  Info,
  PrivacyTip
} from '@mui/icons-material';

export interface ConsentData {
  required: boolean;          // CSV-Analyse (immer true)
  analytics: boolean;         // Erweiterte Analyse
  marketing: boolean;         // Lead-Generierung/Kontakt
  timestamp: Date;
}

export interface FileInfo {
  name: string;
  size: number;
  type: string;
}

interface GDPRConsentModalProps {
  open: boolean;
  onClose: () => void;
  onAccept: (consentData: ConsentData) => void;
  onDecline: () => void;
  fileInfo?: FileInfo;
}

const GDPRConsentModal: React.FC<GDPRConsentModalProps> = ({
  open,
  onClose,
  onAccept,
  onDecline,
  fileInfo
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [consents, setConsents] = useState<ConsentData>({
    required: true,
    analytics: false,
    marketing: false,
    timestamp: new Date()
  });

  const handleAcceptAll = () => {
    const allConsents = {
      ...consents,
      analytics: true,
      marketing: true,
      timestamp: new Date()
    };
    onAccept(allConsents);
  };

  const handleSaveSelection = () => {
    const updatedConsents = {
      ...consents,
      timestamp: new Date()
    };
    onAccept(updatedConsents);
  };

  const handleDecline = () => {
    onDecline();
  };

  const handleConsentChange = (type: 'analytics' | 'marketing') => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setConsents({
      ...consents,
      [type]: event.target.checked
    });
  };

  return (
    <Dialog
      open={open}
      onClose={() => {}} // Prevent backdrop click close
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      aria-labelledby="gdpr-consent-title"
      aria-describedby="gdpr-consent-description"
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 2,
          maxHeight: '90vh'
        }
      }}
    >
      <DialogTitle id="gdpr-consent-title">
        <Box display="flex" alignItems="center" gap={1} mb={1}>
          <Lock color="primary" sx={{ fontSize: 28 }} />
          <Typography variant="h5" component="h2" fontWeight="600">
            Datenschutz und Ihre Einwilligung
          </Typography>
        </Box>
        <Typography variant="subtitle2" color="text.secondary">
          Bevor wir Ihre CSV-Datei {fileInfo && `"${fileInfo.name}"`} analysieren können
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pb: 2 }}>
        <DialogContentText id="gdpr-consent-description" component="div">
          <Box sx={{ mb: 3 }}>
            <Typography paragraph>
              Wir möchten Ihre CSV-Datei analysieren, um Ihnen eine fundierte
              Bewertung der Hybrid-Speicher-Eignung zu erstellen.
            </Typography>

            <Typography paragraph>
              <strong>Dabei verarbeiten wir folgende Daten:</strong>
            </Typography>

            <Box sx={{ pl: 2, mb: 2 }}>
              <Typography variant="body2" paragraph>
                • Leistungsprofile (Strom, Spannung, Leistung)<br/>
                • Zeitstempel für Lastprofil-Analyse<br/>
                • Statistische Kennzahlen (Spitzen- und Mittelwerte)
              </Typography>
            </Box>

            <Box sx={{
              p: 2,
              borderRadius: 2,
              backgroundColor: theme.palette.primary.main + '10',
              border: `1px solid ${theme.palette.primary.main}30`,
              mb: 3
            }}>
              <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Security fontSize="small" color="primary" />
                <strong>Datenschutz-Garantie:</strong>
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                🔒 Verschlüsselte Übertragung (HTTPS)<br/>
                🕐 CSV-Daten werden nach 7 Tagen automatisch gelöscht<br/>
                🇦🇹 Speicherung in deutschen/österreichischen Rechenzentren
              </Typography>
            </Box>
          </Box>
        </DialogContentText>

        <Typography variant="h6" gutterBottom sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <PrivacyTip color="primary" />
          Ihre Einwilligung für folgende Verarbeitungen:
        </Typography>

        <Box sx={{ mb: 3 }}>
          {/* Required consent - cannot be unchecked */}
          <FormControlLabel
            control={
              <Checkbox
                checked={consents.required}
                disabled={true}
                color="primary"
                size="medium"
              />
            }
            label={
              <Box sx={{ ml: 1 }}>
                <Typography variant="body1" fontWeight="600" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Analytics fontSize="small" color="primary" />
                  Erforderliche Datenverarbeitung
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  CSV-Upload und Grundanalyse für technische Bewertung Ihres Lastprofils<br/>
                  <em>Rechtsgrundlage: Art. 6 Abs. 1 lit. a DSGVO (Einwilligung)</em>
                </Typography>
              </Box>
            }
            sx={{ alignItems: 'flex-start', mb: 2 }}
          />

          <Divider sx={{ my: 2 }} />

          {/* Optional analytics consent */}
          <FormControlLabel
            control={
              <Checkbox
                checked={consents.analytics}
                onChange={handleConsentChange('analytics')}
                color="primary"
                size="medium"
              />
            }
            label={
              <Box sx={{ ml: 1 }}>
                <Typography variant="body1" fontWeight="600" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Info fontSize="small" color="secondary" />
                  Erweiterte Analyse (optional)
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Detaillierte Optimierungsempfehlungen und Dimensionierung<br/>
                  <em>Zusätzliche Analysezeit: 2-3 Minuten</em>
                </Typography>
              </Box>
            }
            sx={{ alignItems: 'flex-start', mb: 2 }}
          />

          <Divider sx={{ my: 2 }} />

          {/* Optional marketing consent */}
          <FormControlLabel
            control={
              <Checkbox
                checked={consents.marketing}
                onChange={handleConsentChange('marketing')}
                color="primary"
                size="medium"
              />
            }
            label={
              <Box sx={{ ml: 1 }}>
                <Typography variant="body1" fontWeight="600" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ContactMail fontSize="small" color="secondary" />
                  Kontaktaufnahme durch InnCharge (optional)
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Beratung und Angebotserstellung zu Hybrid-Speicherlösungen<br/>
                  <em>Sie können uns später Ihre Kontaktdaten freiwillig mitteilen</em>
                </Typography>
              </Box>
            }
            sx={{ alignItems: 'flex-start', mb: 2 }}
          />
        </Box>

        <Box sx={{
          p: 2,
          borderRadius: 2,
          backgroundColor: theme.palette.grey[50],
          border: `1px solid ${theme.palette.grey[300]}`,
          mb: 3
        }}>
          <Typography variant="body2" color="text.secondary">
            <strong>Wichtige Informationen:</strong><br/>
            🕒 <strong>Speicherdauer:</strong> CSV-Daten 7 Tage, Kontaktdaten bis zu 7 Jahre (geschäftliche Aufbewahrungspflicht)<br/>
            📧 <strong>Widerruf:</strong> Jederzeit per E-Mail an [Datenschutz E-Mail]<br/>
            ⚖️ <strong>Ihre Rechte:</strong> Auskunft, Berichtigung, Löschung, Einschränkung, Datenübertragbarkeit
          </Typography>
        </Box>

        <Box display="flex" flexWrap="wrap" gap={2} justifyContent="center">
          <Link
            href="/datenschutz"
            target="_blank"
            rel="noopener noreferrer"
            color="primary"
            underline="hover"
            sx={{ fontSize: '0.875rem' }}
          >
            📄 Datenschutzerklärung
          </Link>
          <Link
            href="/impressum"
            target="_blank"
            rel="noopener noreferrer"
            color="primary"
            underline="hover"
            sx={{ fontSize: '0.875rem' }}
          >
            ℹ️ Impressum
          </Link>
        </Box>
      </DialogContent>

      <DialogActions sx={{
        p: 3,
        flexDirection: isMobile ? 'column' : 'row',
        justifyContent: 'space-between',
        gap: 2
      }}>
        <Button
          onClick={handleDecline}
          variant="outlined"
          color="inherit"
          size="large"
          fullWidth={isMobile}
          sx={{ order: isMobile ? 3 : 1 }}
        >
          Ablehnen
        </Button>

        <Box display="flex" gap={2} sx={{
          flexDirection: isMobile ? 'column' : 'row',
          width: isMobile ? '100%' : 'auto',
          order: isMobile ? 1 : 2
        }}>
          <Button
            onClick={handleAcceptAll}
            variant="outlined"
            color="primary"
            size="large"
            fullWidth={isMobile}
          >
            Alle akzeptieren
          </Button>

          <Button
            onClick={handleSaveSelection}
            variant="contained"
            color="primary"
            size="large"
            fullWidth={isMobile}
          >
            Auswahl speichern
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default GDPRConsentModal;